import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { BookOpen, Check, CircleHelp, Code2, Copy, Eye, FileText, type LucideIcon } from "lucide-react";
import {
  helpScreenKeyForPath,
  isHelpLinkPath,
  type HelpInboxReportDetail,
  type HelpInboxReportSummary,
  type HelpSourceKind,
} from "@ttc-spectree/toolbox-contract";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { helpContextScreen, helpContextVersions } from "@/components/toolbox/help/help-send-to-aaron";
import { Lightbox } from "@/components/ui/lightbox";
import { useMe } from "@/hooks/queries/use-me";
import {
  useHelpInboxCount,
  useHelpInboxReport,
  useHelpInboxReports,
  useHelpInboxScreenshotUrl,
  useSetHelpInboxHandled,
} from "@/hooks/queries/use-help-inbox";
import { usePageTitle } from "@/hooks/use-page-title";
import { HELP_SCREEN_LABELS, helpRolesLabel } from "@/lib/toolbox/help";
import { TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Help inbox (epic PI-SPEC-SW-E302, feature PI-SPEC-SW-1559, ER-1655
 * Resolved Decision 5; Claude Design "Toolbox Help Mockups" frame H7), at
 * `/toolbox/help-inbox`, for global Admins only: anyone else is sent to
 * Toolbox home (the API refuses them too).
 *
 * Left: open reports, newest first, each with who sent it, when, the first
 * question, the screen, the screenshot count and "Help was not sure" when an
 * answer said so (that tag is the list of documentation gaps); an Open /
 * Handled toggle. Right: the whole conversation as the person saw it, their
 * note, the screenshots and the context line; **Mark handled** moves it to
 * Handled (it stays readable there) and **Copy as text** gives a plain-text
 * export for an ER or a message. There is no reply from here (v1), and the
 * sender is not told when it is handled. Renders in the Toolbox frame in the
 * app and in SpecTree's chrome in a browser.
 */

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isYesterday(date: Date, now: Date): boolean {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  return sameDay(date, yesterday);
}

function timeOf(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function dayOf(date: Date, now: Date): string {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** The list row's time: "2:14 PM" today, "Yesterday", else "Oct 23". */
export function helpInboxListTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  if (sameDay(date, now)) return timeOf(date);
  if (isYesterday(date, now)) return "Yesterday";
  return dayOf(date, now);
}

/** The detail's time: "today 2:14 PM", "yesterday 11:02 AM", else "Oct 23, 2:14 PM". */
export function helpInboxDetailTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  if (sameDay(date, now)) return `today ${timeOf(date)}`;
  if (isYesterday(date, now)) return `yesterday ${timeOf(date)}`;
  return `${dayOf(date, now)}, ${timeOf(date)}`;
}

/** The screen a report came from, by its route ("Queue"). */
export function helpInboxScreenLabel(route: string): string {
  return HELP_SCREEN_LABELS[helpScreenKeyForPath(route)];
}

/**
 * Copy as text: the whole report in plain words, for pasting into an Epic
 * Request or a message: who, when, screen, role, versions, whether Help was
 * not sure, every message with its sources, the note and the screenshots.
 */
export function helpInboxReportText(report: HelpInboxReportDetail): string {
  const sentAt = new Date(report.createdAt);
  const lines: string[] = ["Help inbox report"];
  const sender = report.sender.email ? `${report.sender.name} (${report.sender.email})` : report.sender.name;
  lines.push(`From: ${sender}`);
  lines.push(`Sent: ${Number.isNaN(sentAt.getTime()) ? report.createdAt : sentAt.toLocaleString()}`);
  lines.push(`Screen: ${helpContextScreen(report.context)} (${report.context.route})`);
  if (report.context.itemId) lines.push(`Item: ${report.context.itemId}`);
  lines.push(`Role: ${helpRolesLabel(report.context.roles)}`);
  const versions = report.context.packVersion
    ? `${helpContextVersions(report.context)} · help pack ${report.context.packVersion}`
    : helpContextVersions(report.context);
  lines.push(`Versions: ${versions}`);
  if (report.helpWasNotSure) lines.push("Help was not sure: yes");
  if (report.handledAt) {
    lines.push(
      `Handled: ${new Date(report.handledAt).toLocaleString()}${report.handledBy ? ` by ${report.handledBy.name}` : ""}`
    );
  }
  lines.push("", "Conversation");
  for (const message of report.conversation) {
    lines.push("", `${message.role === "person" ? report.sender.name : "Help"}:`, message.text.trim());
    if (message.sources && message.sources.length > 0) {
      lines.push(`Sources: ${message.sources.map((source) => source.title).join("; ")}`);
    }
  }
  if (report.note) lines.push("", `Note from ${report.sender.name}:`, report.note.trim());
  if (report.screenshots.length > 0) {
    lines.push(
      "",
      `Screenshots: ${String(report.screenshots.length)} (${report.screenshots.map((shot) => shot.name).join(", ")})`
    );
  }
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

const glass = "rounded-[10px] border border-[#1F1F23] bg-[#111113]";
const chipBase = "inline-flex h-5 flex-none items-center gap-[5px] whitespace-nowrap rounded-full px-2 text-[11px] font-medium";
const chipNone = cn(chipBase, "bg-[#1A1A1E] text-muted-foreground");
const chipWarn = cn(chipBase, "bg-[rgba(245,158,11,0.12)] text-[#FBBF24]");
const smallButton =
  "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[9px] text-xs font-medium text-[#FAFAFA] transition-colors hover:bg-[#222226] disabled:opacity-50";
const smallPrimary =
  "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#E11837] px-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#C8102E] disabled:opacity-50";

const SOURCE_ICONS: Readonly<Record<HelpSourceKind, LucideIcon>> = {
  guide: BookOpen,
  screen: Eye,
  code: Code2,
  doc: FileText,
};

function shotCountText(count: number): string {
  return count === 1 ? "1 screenshot" : `${String(count)} screenshots`;
}

function ReportRow({
  report,
  selected,
  onSelect,
}: {
  report: HelpInboxReportSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        className={cn(
          "flex w-full flex-col gap-1 border-t border-[#18181B] px-3.5 py-2.5 text-left first:border-t-0 hover:bg-[#131316]",
          selected && "bg-[#151518] shadow-[inset_3px_0_0_#E11837] hover:bg-[#151518]"
        )}
        data-testid="help-inbox-row"
      >
        <span className="flex w-full items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{report.sender.name}</span>
          <span className={cn("text-[11px]", TOOLBOX_TEXT.faint)}>{helpInboxListTime(report.createdAt)}</span>
        </span>
        <span className="w-full truncate text-xs text-muted-foreground">{report.firstQuestion}</span>
        <span className="flex flex-wrap gap-1.5">
          <span className={chipNone}>{helpInboxScreenLabel(report.route)}</span>
          {report.screenshotCount > 0 && <span className={chipNone}>{shotCountText(report.screenshotCount)}</span>}
          {report.helpWasNotSure && (
            <span className={chipWarn} data-testid="help-inbox-not-sure">
              Help was not sure
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

function Screenshot({ reportId, index, name }: { reportId: string; index: number; name: string }) {
  const { url, failed } = useHelpInboxScreenshotUrl(reportId, index);
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        disabled={!url}
        onClick={() => {
          setOpen(true);
        }}
        className="relative flex h-[124px] w-[200px] flex-none items-end overflow-hidden rounded-md border border-[#3F3F46] bg-[#141417] p-1.5 text-left"
        aria-label={`Open ${name} full size`}
        data-testid="help-inbox-shot"
      >
        {url && <img src={url} alt={name} className="absolute inset-0 h-full w-full object-cover" />}
        <span className="relative max-w-full truncate rounded-[3px] bg-[rgba(9,9,11,0.85)] px-1 font-mono text-[10px] text-[#D4D4D8]">
          {failed ? "Could not load" : name}
        </span>
      </button>
      {url && <Lightbox open={open} onOpenChange={setOpen} src={url} alt={name} />}
    </div>
  );
}

function Detail({ report, onHandled }: { report: HelpInboxReportDetail; onHandled: () => void }) {
  const setHandled = useSetHelpInboxHandled();
  const [copy, setCopy] = useState<"idle" | "copied" | "failed">("idle");
  const [handleError, setHandleError] = useState<string | null>(null);

  useEffect(() => {
    if (copy === "idle") return undefined;
    const timer = setTimeout(() => {
      setCopy("idle");
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [copy]);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(helpInboxReportText(report));
      setCopy("copied");
    } catch {
      setCopy("failed");
    }
  };

  const markHandled = () => {
    setHandleError(null);
    setHandled.mutate(
      { id: report.id, handled: true },
      {
        onSuccess: onHandled,
        onError: () => {
          setHandleError("It was not marked handled. Try again.");
        },
      }
    );
  };

  const context = report.context;
  const who = [report.sender.name, helpRolesLabel(context.roles), helpContextScreen(context)].join(" · ");

  return (
    <div className={cn(glass, "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden")} data-testid="help-inbox-detail">
      <div className="flex flex-none items-center gap-2.5 border-b border-[#1F1F23] px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="truncate font-display text-sm font-semibold leading-[1.3]">{report.firstQuestion}</h2>
          <span className="truncate text-xs text-muted-foreground">{who}</span>
          <span className={cn("truncate text-xs", TOOLBOX_TEXT.faint)} data-testid="help-inbox-context">
            {helpContextVersions(context)} · {helpInboxDetailTime(report.createdAt)}
            {report.handledAt &&
              ` · Handled ${helpInboxDetailTime(report.handledAt)}${report.handledBy ? ` by ${report.handledBy.name}` : ""}`}
          </span>
        </div>
        <button
          type="button"
          className={smallButton}
          onClick={() => void copyText()}
          data-testid="help-inbox-copy"
        >
          <Copy className="h-[13px] w-[13px]" aria-hidden="true" />
          {copy === "copied" ? "Copied" : copy === "failed" ? "Could not copy" : "Copy as text"}
        </button>
        {!report.handledAt && (
          <button
            type="button"
            className={smallPrimary}
            onClick={markHandled}
            disabled={setHandled.isPending}
            data-testid="help-inbox-mark-handled"
          >
            <Check className="h-[13px] w-[13px]" aria-hidden="true" />
            Mark handled
          </button>
        )}
      </div>
      {handleError && (
        <span role="alert" className="px-4 pt-2 text-[13px] text-[#FBBF24]">
          {handleError}
        </span>
      )}
      <div className="flex min-h-0 flex-1 gap-4 overflow-y-auto px-4 py-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-3" data-testid="help-inbox-conversation">
          {report.conversation.map((message, index) =>
            message.role === "person" ? (
              <div
                key={`person-${String(index)}`}
                className="flex max-w-[420px] flex-col self-start whitespace-pre-wrap break-words rounded-[12px_12px_12px_4px] border border-[#27272A] bg-[#1A1A1E] px-3 py-[9px] text-[13px] leading-[1.5] text-[#FAFAFA]"
              >
                {message.text}
              </div>
            ) : (
              <div key={`help-${String(index)}`} className="flex flex-col gap-2 text-[13px] leading-[1.55] text-[#E4E4E7]">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.02em] text-muted-foreground">
                  <span className="flex h-4 w-4 items-center justify-center rounded-[5px] border border-[#27272A] bg-[#1A1A1E]">
                    <CircleHelp className="h-[11px] w-[11px] text-[#D4D4D8]" aria-hidden="true" />
                  </span>
                  Help
                </span>
                <MarkdownRenderer
                  content={message.text}
                  allowLink={isHelpLinkPath}
                  className="[&_ol]:list-outside [&_ol]:pl-[18px] [&_p]:mb-2 [&_ul]:list-outside [&_ul]:pl-[18px]"
                />
                {message.sources && message.sources.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" aria-label="Sources">
                    {message.sources.map((source, sourceIndex) => {
                      const Icon = SOURCE_ICONS[source.kind];
                      return (
                        <span
                          key={`${source.title}-${String(sourceIndex)}`}
                          className="inline-flex h-[22px] max-w-full items-center gap-[5px] rounded-md border border-[#1F1F23] bg-[#151518] px-2 text-[11px] text-muted-foreground"
                        >
                          <Icon className="h-3 w-3 flex-none" aria-hidden="true" />
                          <span className="truncate">{source.title}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          )}
          {report.note && (
            <div className={cn(glass, "flex flex-col gap-1.5 px-3 py-2.5")} data-testid="help-inbox-note">
              <span className={TOOLBOX_TEXT.eyebrow}>Note from {report.sender.name.split(" ")[0]}</span>
              <span className="whitespace-pre-wrap break-words text-[13px] text-[#E4E4E7]">{report.note}</span>
            </div>
          )}
        </div>
        {report.screenshots.length > 0 && (
          <div className="flex w-[200px] flex-none flex-col gap-2">
            <span className={TOOLBOX_TEXT.eyebrow}>{report.screenshots.length === 1 ? "Screenshot" : "Screenshots"}</span>
            {report.screenshots.map((shot) => (
              <Screenshot key={shot.index} reportId={report.id} index={shot.index} name={shot.name} />
            ))}
            <span className="text-xs text-muted-foreground">Click to open full size</span>
          </div>
        )}
      </div>
    </div>
  );
}

type InboxView = "open" | "handled";

function HelpInbox() {
  const [view, setView] = useState<InboxView>("open");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const reports = useHelpInboxReports(view);
  const { data: openCount } = useHelpInboxCount(true);
  const list = reports.data ?? [];
  const selected = list.find((report) => report.id === selectedId) ?? list[0] ?? null;
  const detail = useHelpInboxReport(selected?.id ?? null);

  const segment = (value: InboxView, label: string) => (
    <button
      type="button"
      aria-pressed={view === value}
      onClick={() => {
        setView(value);
        setSelectedId(null);
      }}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 font-medium",
        view === value ? "bg-[#1F1F23] text-[#FAFAFA]" : "text-muted-foreground hover:text-[#FAFAFA]"
      )}
      data-testid={`help-inbox-view-${value}`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex min-h-[560px] w-full flex-1 flex-col text-[13px] text-[#FAFAFA]" data-testid="help-inbox-page">
      <div className="flex flex-none items-center gap-3 px-5 pb-3 pt-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className={TOOLBOX_PAGE_TITLE}>Help inbox</h1>
          <span className="text-xs text-muted-foreground">Conversations people sent from Help · only Admins see this</span>
        </div>
        <span className="flex-1" />
        <span className="inline-flex flex-none gap-0.5 rounded-[9px] border border-[#1F1F23] bg-[#111113] p-[3px]">
          {segment("open", openCount && openCount > 0 ? `Open ${String(openCount)}` : "Open")}
          {segment("handled", "Handled")}
        </span>
      </div>
      <div className="flex min-h-0 flex-1 gap-3 px-5 pb-5">
        <div className={cn(glass, "w-[300px] flex-none overflow-y-auto")} data-testid="help-inbox-list">
          {reports.isLoading && <p className={cn("px-3.5 py-3 text-[13px]", TOOLBOX_TEXT.faint)}>Loading…</p>}
          {reports.isError && (
            <p role="alert" className="px-3.5 py-3 text-[13px] text-[#FBBF24]">
              The inbox could not be loaded. Reload the page to try again.
            </p>
          )}
          {!reports.isLoading && !reports.isError && list.length === 0 && (
            <p className="px-3.5 py-3 text-[13px] text-muted-foreground" data-testid="help-inbox-empty">
              {view === "open"
                ? "Nothing open. What people send from Help appears here."
                : "Nothing handled yet."}
            </p>
          )}
          {list.length > 0 && (
            <ul>
              {list.map((report) => (
                <ReportRow
                  key={report.id}
                  report={report}
                  selected={report.id === selected?.id}
                  onSelect={() => {
                    setSelectedId(report.id);
                  }}
                />
              ))}
            </ul>
          )}
        </div>
        {selected && detail.data && (
          <Detail
            key={detail.data.id}
            report={detail.data}
            onHandled={() => {
              setSelectedId(null);
            }}
          />
        )}
        {selected && detail.isLoading && (
          <div className={cn(glass, "flex flex-1 items-center justify-center", TOOLBOX_TEXT.faint)}>Opening…</div>
        )}
        {selected && detail.isError && (
          <div role="alert" className={cn(glass, "flex flex-1 items-center justify-center text-[#FBBF24]")}>
            That report could not be opened.
          </div>
        )}
      </div>
    </div>
  );
}

/** `/toolbox/help-inbox`: global Admins only; anyone else goes to Toolbox home. */
export function ToolboxHelpInboxPage() {
  usePageTitle("Help inbox · SpecTree Toolbox");
  const { data: me, isLoading } = useMe();
  if (isLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center" role="status" aria-live="polite">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }
  if (me?.isGlobalAdmin !== true) return <Navigate to={TOOLBOX_HOME_PATH} replace />;
  return <HelpInbox />;
}
